import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from "constants/index";

import React, { FC, ForwardedRef, useEffect, useState } from "react";

import AutocompleteHookForm from "components/Autocomplete/Autocomplete.hookform";
import { MultiAutocompleteHookForm } from "components/MultiAutocomplete";
import {
  TemplateEntity,
  useGetManyBaseTemplateControllerTemplateEntity,
} from "generated";
import { useDebounce } from "hooks/useDebounce";

export interface SelectProps {
  name: string;
  label: string;
  require?: boolean;
  multi?: boolean;
  disabled?: boolean;
  clientId?: string;
  templateTypeId?: string;
  onChange?: (value: any) => void;
}

type TemplateSelectFormProps = SelectProps;

const TemplateSelectForm: FC<TemplateSelectFormProps> = React.forwardRef<
  HTMLSelectElement,
  TemplateSelectFormProps
>((props: TemplateSelectFormProps, ref: ForwardedRef<HTMLSelectElement>) => {
  const {
    name,
    label,
    require,
    multi,
    disabled,
    clientId,
    templateTypeId,
    onChange,
  } = props;

  const [keyword, setKeyword] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [listOptions, setListOptions] =
    useState<{ label: string; value: unknown }[]>();

  const { data } = useGetManyBaseTemplateControllerTemplateEntity({
    s: JSON.stringify({
      ...(keyword
        ? {
            name: {
              $contL: keyword,
            },
          }
        : {}),
      ...(clientId
        ? {
            clientId: {
              $eq: clientId,
            },
          }
        : {}),
      ...(templateTypeId
        ? {
            templateTypeId: {
              $eq: templateTypeId,
            },
          }
        : {}),
      status: {
        $eq: "Active",
      },
    }),
    join: [`templateType`],
    limit: DEFAULT_PAGE_SIZE,
    offset: DEFAULT_PAGE_INDEX,
  });

  // debounce
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  useEffect(() => {
    setKeyword(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const handleSearchSelection = (value: string) => {
    setSearchTerm(value);
  };
  const handleResetData = (dataQuery: TemplateEntity[]) => {
    let options: { label: string; value: unknown }[] = [];
    if (dataQuery && dataQuery.length > 0) {
      options = dataQuery.map((item) => ({
        value: item?.id,
        label: item?.name,
        info: item,
      }));
    }
    setListOptions(options);
  };
  useEffect(() => {
    handleResetData(data?.data?.data ?? []);
  }, [data]);

  return multi ? (
    <MultiAutocompleteHookForm
      name={name}
      label={label}
      placeholder="Select an option"
      options={Array(10)
        .fill(null)
        .map((_i, index) => ({
          label: `Option ${index + 1}`,
          value: index + 1,
        }))}
      onInputChange={handleSearchSelection}
      required={require}
      disabled={disabled}
    />
  ) : (
    <AutocompleteHookForm
      name={name}
      label={label}
      placeholder="Select an option"
      options={listOptions ?? []}
      onInputChange={handleSearchSelection}
      required={require}
      disabled={disabled}
      onChange={onChange}
    />
  );
});
export default TemplateSelectForm;
